// src/ui/features/calculator/pages/Calculator.jsx
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useApp } from "../../../state/Context";
import { useAuth } from "../../../state/AuthContext";

import AuthModal from "../../app/auth/AuthModal";

import SidePanel from "../components/SidePanel";
import CalculatorHeaderCard from "../components/CalculateHeaderCard";
import ModulesPanel from "../components/ModulesPanel";
import UpgradeModal from "../components/UpgradeModal";

import { titleCaseDegree, downloadCsv, toEngineInputs } from "../utils/calculatorUtils";

import { useProGate } from "../hooks/useProGate";
import { useRuleset } from "../hooks/useRuleset";
import { useCalculatorEngine } from "../hooks/useCalculatorEngine";

import { createCheckoutSession } from "../../../firebase/functions";
import CalculatorTopBar from "../components/CalculatorTopBar";
import ModuleCalcPanel from "../components/ModuleCalcPanel";
import { importCsv as importCsvApi, exportCsv as exportCsvApi, getCatalog } from "../../../firebase/functions";

export default function Calculator() {
  const { selection, setSelection, incrementCalculations, freeCalcCount } = useApp();
  const university = selection?.university;
  const degreeType = selection?.degreeLevel;

  const { isSignedIn, isPro } = useAuth();
  const pro = useProGate({ isSignedIn, isPro });

  const pendingImportRunRef = useRef(null);

  const [upgradeReason, setUpgradeReason] = useState(null);

  const compareCreditsTriState = useCallback((modulesByLevel, req) => {
    const sumCredits = (mods) =>
      (mods || []).reduce((acc, m) => {
        const n = Number(m?.credits);
        return acc + (Number.isFinite(n) ? n : 0);
      }, 0);

    const requiredKeys = Object.keys(req || {}).filter((k) => Number(req[k]) > 0);

    let sawUnder = false;
    let sawOver = false;

    for (const levelKey of requiredKeys) {
      const have = sumCredits(modulesByLevel?.[levelKey]);
      const need = Number(req[levelKey]) || 0;

      if (have < need) sawUnder = true;
      else if (have > need) sawOver = true;
    }

    if (sawUnder) return true;
    if (sawOver) return null;
    return false;
  }, []);

  const rules = useRuleset({
    universityId: university?.id,
    degreeType,
  });

  const FREE_CALC_LIMIT = 5;

  const engine = useCalculatorEngine({
    university,
    degreeType,
    ruleset: rules.ruleset,
    modulesByLevel: rules.modulesByLevel,
    setModulesByLevel: rules.setModulesByLevel,
    targetGrade: rules.targetGrade,
    setTargetGrade: rules.setTargetGrade,
    incrementCalculations,
    canUseProFeatures: pro.canUseProFeatures,
    isPro,
    freeCalcCount,
    freeCalcLimit: FREE_CALC_LIMIT,
    onRequireUpgrade: () => {
      setUpgradeReason("free_limit");

      if (!isSignedIn) {
        pro.setProGateMsg(
          `You’ve reached the maximum of ${FREE_CALC_LIMIT} free calculations. Please sign in to continue.`
        );
        pro.setAuthOpen(true);
        return;
      }

      // signed in but not pro
      pro.setProGateMsg(
        `You’ve reached the maximum of ${FREE_CALC_LIMIT} free calculations. Upgrade to Pro to unlock unlimited calculations.`
      );
      pro.setUpgradeOpen(true);
    },
  });

  const [mode, setMode] = useState("degree");

  const goBack = () =>
    setSelection({ step: "select", university: null, degreeLevel: null });

  const headerTitle = useMemo(() => {
    if (!university?.name || !degreeType) return "Calculator";
    return `${university.name} • ${titleCaseDegree(degreeType)}`;
  }, [university?.name, degreeType]);

  const onExportInner = async () => {
    const inputs = toEngineInputs(rules.modulesByLevel);

    const { filename, csvText } = await exportCsvApi({
      universityId: university.id,
      degreeType,
      inputs,
      targetGrade: rules.targetGrade,
    });

    downloadCsv(filename, csvText);
  };

  const {
    loading: rulesLoading,
    ruleset,
    modulesByLevel,
    setModulesByLevel,
    setTargetGrade,
  } = rules;

  const { requiredCreditsByLevel, onCalculate, onAnalyseCommit } = engine;

  useEffect(() => {
    const pending = pendingImportRunRef.current;
    if (!pending) return;

    const onRightSelection =
      String(university?.id || "") === String(pending.universityId) &&
      String(degreeType || "").toLowerCase() ===
        String(pending.degreeType || "").toLowerCase();

    if (!onRightSelection) return;
    if (rulesLoading || !ruleset) return;

    if (!pending.applied) {
      setModulesByLevel(pending.modulesByLevel);
      setTargetGrade(pending.targetGrade);
      pending.applied = true;
      return;
    }

    const req = requiredCreditsByLevel;
    if (!req || typeof req !== "object" || Object.keys(req).length === 0) return;
    if (pending.ran) return;

    const creditState = compareCreditsTriState(modulesByLevel, req);

    if (creditState === true) {
      onAnalyseCommit?.(pending.targetGrade);
    } else if (creditState === false) {
      onCalculate?.();
    }

    pending.ran = true;
    pendingImportRunRef.current = null;
  }, [
    university?.id,
    degreeType,
    rulesLoading,
    ruleset,
    modulesByLevel,
    setModulesByLevel,
    setTargetGrade,
    requiredCreditsByLevel,
    onCalculate,
    onAnalyseCommit,
    compareCreditsTriState,
  ]);

  const onImportInner = async () => {
    try {
      if (!isSignedIn) {
        setUpgradeReason(null);
        pro.setProGateMsg("Please sign in to use Import.");
        pro.setAuthOpen(true);
        return;
      }
      if (!isPro) {
        setUpgradeReason(null);
        pro.setProGateMsg("Upgrade to Pro to unlock Import.");
        pro.setUpgradeOpen(true);
        return;
      }

      const inputEl = document.createElement("input");
      inputEl.type = "file";
      inputEl.accept = ".csv,text/csv";
      inputEl.multiple = false;

      const file = await new Promise((resolve) => {
        inputEl.onchange = () => resolve(inputEl.files?.[0] || null);
        inputEl.click();
      });

      if (!file) return;

      const csvText = await file.text();
      const resp = await importCsvApi({ csvText });

      if (!resp?.modulesByLevel || typeof resp.modulesByLevel !== "object") {
        throw new Error("Import failed: invalid response (modulesByLevel missing).");
      }
      if (!resp?.universityId || !resp?.degreeType) {
        throw new Error("Import failed: invalid response (universityId/degreeType missing).");
      }

      const importedTarget =
        Number.isFinite(Number(resp.targetGrade)) ? Number(resp.targetGrade) : 70;

      pendingImportRunRef.current = {
        universityId: String(resp.universityId),
        degreeType: String(resp.degreeType).toLowerCase(),
        modulesByLevel: resp.modulesByLevel,
        targetGrade: importedTarget,
        applied: false,
        ran: false,
      };

      const currentUniId = String(university?.id || "");
      const currentDeg = String(degreeType || "").toLowerCase();
      const nextUniId = String(resp.universityId);
      const nextDeg = String(resp.degreeType).toLowerCase();

      const needsSwitch = currentUniId !== nextUniId || currentDeg !== nextDeg;

      if (needsSwitch) {
        const catalog = await getCatalog();
        const uniObj = (catalog?.universities || []).find(
          (u) => String(u.id) === nextUniId
        );

        if (!uniObj) {
          pendingImportRunRef.current = null;
          throw new Error(`Import failed: universityId "${nextUniId}" not found/enabled.`);
        }

        rules.setModulesByLevel({});
        rules.setTargetGrade(importedTarget);

        setSelection({ step: "calc", university: uniObj, degreeLevel: nextDeg });
        return;
      }

      rules.setModulesByLevel(resp.modulesByLevel);
      rules.setTargetGrade(importedTarget);
      pendingImportRunRef.current.applied = true;
    } catch (e) {
      const status = e?.status;
      const msg = e?.message || "Failed to import CSV.";

      if (status === 401) {
        setUpgradeReason(null);
        pro.setProGateMsg("Please sign in to use Import.");
        pro.setAuthOpen(true);
        return;
      }
      if (status === 403 || /pro required/i.test(msg)) {
        setUpgradeReason(null);
        pro.setProGateMsg("Upgrade to Pro to unlock Import.");
        pro.setUpgradeOpen(true);
        return;
      }

      console.error(e);
      alert(msg);
    }
  };

  const onImport = () => pro.requirePro(onImportInner);
  const onExport = () => pro.requirePro(onExportInner);

  const buildPresetModules = useCallback((requiredCredits, minGrade) => {
    const req = Number(requiredCredits) || 0;
    const chunk = 15;

    if (req <= 0) return [];

    const full = Math.floor(req / chunk);
    const rem = req % chunk;

    const mods = [];
    for (let i = 0; i < full; i++) mods.push({ grade: minGrade, credits: chunk });
    if (rem > 0) mods.push({ grade: minGrade, credits: rem });

    return mods;
  }, []);

  const onLoadPresetLevel = useCallback(
    (levelKey, required) => {
      setModulesByLevel((prev) => ({
        ...(prev || {}),
        [levelKey]: buildPresetModules(required, engine.minGrade),
      }));
    },
    [setModulesByLevel, buildPresetModules, engine.minGrade]
  );

  const onClearAllLevel = useCallback(
    (levelKey) => {
      setModulesByLevel((prev) => ({
        ...(prev || {}),
        [levelKey]: [],
      }));
    },
    [setModulesByLevel]
  );

  return (
    <div className="space-y-6">
      <CalculatorTopBar
        onBack={goBack}
        mode={mode}
        onToggleMode={setMode}
        isPro={isPro}
        onRequestPro={pro.onRequestPro}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px] lg:items-start">
        <div className="space-y-6">
          <CalculatorHeaderCard
            headerTitle={headerTitle}
            ruleset={rules.ruleset}
            loading={rules.loading}
            loadError={rules.loadError}
          />

          {mode === "module" ? (
            <ModuleCalcPanel />
          ) : (
            <ModulesPanel
              ruleset={rules.ruleset}
              loading={rules.loading}
              loadError={rules.loadError}
              levels={engine.levels}
              modulesByLevel={rules.modulesByLevel}
              totals={engine.totals}
              requiredCreditsByLevel={engine.requiredCreditsByLevel}
              minGrade={engine.minGrade}
              openLevelKey={rules.openLevelKey}
              setOpenLevelKey={rules.setOpenLevelKey}
              addModule={engine.addModule}
              updateModule={engine.updateModule}
              clampAndAnalyse={engine.clampAndAnalyse}
              removeModule={engine.removeModule}
              error={engine.error}
              onLoadPresetLevel={onLoadPresetLevel}
              onClearAllLevel={onClearAllLevel}
            />
          )}
        </div>

        <SidePanel
          currentDisplayText={engine.result ? `${engine.result.average}%` : "0.00%"}
          currentSubText={
            engine.result
              ? `${engine.result?.classification?.name ?? ""}${
                  engine.result?.classification?.upgraded ? " (Upgraded)" : ""
                }`
              : ""
          }
          onSave={engine.onCalculate}
          onExport={onExport}
          onImport={onImport}

          saveDisabled={engine.calculateDisabled}

          minGrade={engine.minDegreeGrade}
          targetGrade={rules.targetGrade}
          onChangeTarget={rules.setTargetGrade}
          onCommitTarget={(next) => {
            if (!isSignedIn) {
              setUpgradeReason(null);
              pro.setProGateMsg("Please sign in to use Analyse and Analysis.");
              pro.setAuthOpen(true);
              return;
            }
            if (!isPro) {
              setUpgradeReason(null);
              pro.setProGateMsg("Upgrade to Pro to unlock Analyse and Analysis.");
              pro.setUpgradeOpen(true);
              return;
            }
            engine.onAnalyseCommit(next);
          }}
          targetHelperText={engine.targetHelperText}
          analysis={pro.canUseProFeatures ? engine.analysis : null}
          analysisError={pro.canUseProFeatures ? engine.analysisError : ""}
          moduleStats={engine.moduleStats}
          proLocked={pro.proLocked}
          proLockText={pro.proLockText}
          onRequestPro={pro.onRequestPro}
          analysisLoading={engine.analysisLoading}
          calculateLoading={engine.calculateLoading}
        />
      </div>

      <AuthModal
        open={pro.authOpen}
        onClose={() => pro.setAuthOpen(false)}
        brandName="DegreeClassifyUK"
      />

      <UpgradeModal
        open={pro.upgradeOpen}
        onClose={() => {
          pro.setUpgradeOpen(false);
          setUpgradeReason(null);
        }}
        title={upgradeReason === "free_limit" ? "Free limit reached" : "Upgrade to Pro"}
        subtitle={
          upgradeReason === "free_limit"
            ? "You’ve used all free calculations. Upgrade to continue."
            : "Unlock the following premium features for life:"
        }
        bullets={[
          "Unlimited calculations",
          "Target Grade Analysis",
          "Export & Import Grades",
          "Module Calculator",
        ]}
        message={pro.proGateMsg || `You’ve reached the maximum of ${FREE_CALC_LIMIT} free calculations.`}
        primaryText={upgradeReason === "free_limit" ? "Upgrade now" : "View pricing"}
        onViewPricing={async () => {
          const { url } = await createCheckoutSession();
          window.location.assign(url);
        }}
      />
    </div>
  );
}